/**
 * Client-side draft shape for one question block in the custom-competition
 * creation flow (PLAN.md Phase 10.3/10.4) — shared between
 * `CreateCustomCompetitionScreen` (which manages an array of these, one per
 * "+"-added question) and `AddQuestionScreen` (which manages exactly one),
 * so the two never drift on what counts as "ready to submit".
 *
 * NOT A QUIZ: the organizer does not know the correct answer yet, so a
 * block only ever collects the answer LABELS, a points RANGE for
 * `per_answer` (never a value per answer), an optional deadline, and
 * whether locking is allowed — never which answer is right. Same idea for
 * `football`: the organizer picks (or types) the two teams, never a score —
 * the real result is entered later, at reveal.
 *
 * Two real kinds exist: `multiple_choice` and `football` — `kind: ''` is
 * just the unselected state of the "Question type" dropdown.
 */
import type {
  DateScoringMode,
  QuestionDisplayFormat,
  QuestionDraft,
  QuestionResultsFormat,
  ScoringMode,
} from '../api.js';

/**
 * One answer's cosmetics, only rendered when the question's `displayFormat`
 * is `card`. Kept (and still submitted) while the format is `list` so
 * toggling between the two never silently discards what was configured.
 *
 * `''` is the unset value for all three, since these are `<input>` values.
 * `color` is otherwise a `#rrggbb` string — the API rejects anything else.
 * `emoji` is short free text, so an emoji pair ("👶💙") works. `imageUrl` is
 * filled in by `api.uploadQuestionImage` once a file is chosen, never typed.
 *
 * Its own type because the organizer can also edit exactly these three
 * fields *after* creation, on `EditQuestionResultsScreen`, where there is no
 * draft to speak of — one `AnswerAppearanceEditor` serves both.
 */
export interface AnswerAppearance {
  color: string;
  emoji: string;
  imageUrl: string;
}

export interface AnswerDraft extends AnswerAppearance {
  /** Client-only React list key — never sent to the server. */
  key: string;
  label: string;
}

export interface QuestionBlockDraft {
  key: string;
  kind: 'multiple_choice' | 'football' | 'date' | '';
  prompt: string;
  scoringMode: ScoringMode;
  /** How the answers are laid out for everyone once saved. `multiple_choice` only. */
  displayFormat: QuestionDisplayFormat;
  /** How the question looks once revealed — independent of `displayFormat`. `multiple_choice` only. */
  resultsFormat: QuestionResultsFormat;
  answers: AnswerDraft[];
  /** Raw input strings; parsed to integers at submit time. Only meaningful when scoringMode is "per_answer". */
  minPoints: string;
  maxPoints: string;
  /** Raw <input type="date"> / <input type="time"> values. Both empty means "no deadline". */
  deadlineDate: string;
  deadlineTime: string;
  allowLockForever: boolean;

  // --- `date`-kind only ----------------------------------------------------

  /** How closeness becomes points. `placement` (the default) pays nothing at all. */
  dateScoringMode: DateScoringMode;
  /** Raw `<input type="month">` value (`YYYY-MM`) — which month the calendar opens on. Empty means the current month. */
  dateTargetMonth: string;

  // --- `football`-kind only ------------------------------------------------

  /** "database": browse our own already-seeded matches. "custom": organizer types both team names. */
  footballSource: 'database' | 'custom';
  /** The football competition being browsed — chosen before a team, never sent to the server itself. */
  footballCompetitionId: string;
  /** The team being browsed — chosen before a match, never sent to the server itself. */
  footballTeamId: string;
  /** The real match this question predicts, once browsed down to one. */
  footballMatchId: string;
  /** Organizer-typed team names, `source: 'custom'` only. */
  footballHomeTeamName: string;
  footballAwayTeamName: string;
  footballCanGoToPenalties: boolean;

  /**
   * "Advanced settings" — the same `MatchParams` a real match's ruleset
   * stage assigns it (see `api.ts`'s `FootballScoringDraft` doc comment).
   * Raw input strings, all optional: left blank, the server falls back to
   * a group-stage-like default — this is what lets the section stay
   * collapsed by default rather than demanding seven values up front.
   */
  footballOutcome: string;
  footballExact: string;
  footballPotMultiplier: string;
  footballDecay: string;
  footballThreshold: string;
  footballPenaltyExact: string;
  footballPenaltyWrong: string;
}

/** Mirrors the API's own colour validation (`routes/custom.ts`'s `hexColorSchema`). */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export const MIN_ANSWERS = 2;
export const MAX_ANSWERS = 10;

let nextKey = 0;
/** A short, stable-enough client key — never persisted, just for React lists. */
function freshKey(): string {
  nextKey += 1;
  return `draft-${nextKey}`;
}

export function newAnswerDraft(overrides: Partial<Omit<AnswerDraft, 'key'>> = {}): AnswerDraft {
  return { key: freshKey(), label: '', color: '', emoji: '', imageUrl: '', ...overrides };
}

export function newQuestionBlockDraft(): QuestionBlockDraft {
  return {
    key: freshKey(),
    kind: '',
    prompt: '',
    // Defaults to the first choice rather than forcing an explicit pick
    // before any other field is usable — freely changeable, never submitted
    // until the block itself is filled in.
    scoringMode: 'per_answer',
    // The layout every question had before this was configurable, so an
    // organizer who never touches the setting gets exactly what they got
    // before it existed.
    displayFormat: 'list',
    resultsFormat: 'list',
    answers: [newAnswerDraft(), newAnswerDraft()],
    minPoints: '',
    maxPoints: '',
    deadlineDate: '',
    deadlineTime: '',
    allowLockForever: false,
    // Ranks and medals, no points — the least presumptuous default, since it
    // commits the organizer to no stakes they haven't chosen.
    dateScoringMode: 'placement',
    dateTargetMonth: '',
    footballSource: 'database',
    footballCompetitionId: '',
    footballTeamId: '',
    footballMatchId: '',
    footballHomeTeamName: '',
    footballAwayTeamName: '',
    footballCanGoToPenalties: false,
    footballOutcome: '',
    footballExact: '',
    footballPotMultiplier: '',
    footballDecay: '',
    footballThreshold: '',
    footballPenaltyExact: '',
    footballPenaltyWrong: '',
  };
}

/** The colours the Baby Shower preset ships with — a blue and a pink card, matching the design it was drawn from. */
const BABY_SHOWER_BOY_COLOR = '#3b82f6';
const BABY_SHOWER_GIRL_COLOR = '#ec4899';

/**
 * The one question a "Baby Shower" competition starts with, prefilled
 * (PLAN.md Phase 10.11).
 *
 * It is an ordinary `multiple_choice` block, not a special kind: card
 * format, two coloured answers with emoji, and winner/loser scoring — every
 * one of which an organizer can also reach by hand from the plain Custom
 * Competition path. That is deliberate. The preset is a shortcut through the
 * same form, so it can never drift into a shape the custom editor cannot
 * produce, and everything in it stays editable before submitting.
 *
 * The prompt and answer labels are passed in already translated: they become
 * the organizer's own stored words the moment they submit (question text is
 * never per-locale content — see PLAN.md Phase 10.4), so what matters is
 * only that the *starting point* reads in their language.
 */
export function newBabyShowerBlockDraft(text: {
  prompt: string;
  boy: string;
  girl: string;
}): QuestionBlockDraft {
  return {
    ...newQuestionBlockDraft(),
    kind: 'multiple_choice',
    prompt: text.prompt,
    // "Just winner or loser, no points system" — the default this preset was
    // specified with; the organizer can still switch to a points range.
    scoringMode: 'winner_loser',
    displayFormat: 'card',
    // The reveal-day design this preset was drawn from: big per-answer
    // counts, a crown over whichever turned out correct, and the names.
    resultsFormat: 'panels',
    answers: [
      newAnswerDraft({ label: text.boy, color: BABY_SHOWER_BOY_COLOR, emoji: '👶💙' }),
      newAnswerDraft({ label: text.girl, color: BABY_SHOWER_GIRL_COLOR, emoji: '👧💗' }),
    ],
  };
}

/**
 * A "MOMMY or DADDY?" question — the Baby Shower template's second, longer
 * half (PLAN.md Phase 10.13). Same two answers every time, so only the
 * prompt varies.
 *
 * Scored `per_answer` with a 0–1 range rather than `winner_loser`: these are
 * a guessing game with a right answer worth a point, unlike the gender
 * question which is a single celebratory coin-flip. The organizer still marks
 * *which* answer was right only at reveal — the range is a promise about the
 * stakes, not an answer key (see the module doc in `custom/service.ts`).
 *
 * `🥸` stands in for the moustache the design draws: Unicode has no
 * standalone moustache emoji, and "face with glasses and moustache" is the
 * closest single character. Easily changed per answer in the editor.
 */
export function newMommyDaddyBlockDraft(text: {
  prompt: string;
  mommy: string;
  daddy: string;
}): QuestionBlockDraft {
  return {
    ...newQuestionBlockDraft(),
    kind: 'multiple_choice',
    prompt: text.prompt,
    scoringMode: 'per_answer',
    minPoints: '0',
    maxPoints: '1',
    displayFormat: 'card',
    // Unlike the gender question, these shouldn't show a live vote count on
    // the card — just a result badge once revealed, with everyone's pick
    // (and what it earned them) tucked behind "View predictions" same as any
    // `list`-format question.
    resultsFormat: 'list',
    answers: [
      newAnswerDraft({ label: text.mommy, color: BABY_SHOWER_GIRL_COLOR, emoji: '💋' }),
      newAnswerDraft({ label: text.daddy, color: BABY_SHOWER_BOY_COLOR, emoji: '🥸' }),
    ],
  };
}

/**
 * The Baby Shower template's "when will the baby be born?" question (PLAN.md
 * Phase 10.16) — a calendar guess rather than a choice between answers.
 *
 * Ships in `placement` mode: 1st/2nd/3rd with medals and no points, which is
 * what the design's own leaderboard shows and keeps the celebratory question
 * from quietly outweighing the fifteen MOMMY/DADDY ones on the bolão total. An
 * organizer who wants it to count can switch to any of the three point modes in
 * the editor.
 *
 * `dateTargetMonth` is left empty deliberately: only the parents know the due
 * month, so the calendar opens on the current one and the organizer sets it if
 * they care to. Everything else about the question is the same shortcut through
 * the same editor every other preset block is.
 */
export function newBirthDateBlockDraft(text: { prompt: string }): QuestionBlockDraft {
  return {
    ...newQuestionBlockDraft(),
    kind: 'date',
    prompt: text.prompt,
    dateScoringMode: 'placement',
    resultsFormat: 'leaderboard',
  };
}

function isIntegerString(value: string): boolean {
  return value.trim() !== '' && Number.isInteger(Number(value));
}

/** Any non-negative number, integer or decimal — used for the advanced-settings fields, some of which (decay, pot multiplier) aren't integers. */
function isNonNegativeNumberString(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
}

const FOOTBALL_SCORING_FIELDS = [
  'footballOutcome',
  'footballExact',
  'footballPotMultiplier',
  'footballDecay',
  'footballThreshold',
  'footballPenaltyExact',
  'footballPenaltyWrong',
] as const;

/** Whether a block has enough filled in to be worth sending to the server. */
export function isQuestionBlockValid(block: QuestionBlockDraft): boolean {
  // A deadline is optional, but a half-filled one (date with no time, or
  // vice versa) is worth blocking rather than silently dropping — shared by
  // every kind, so check it once up front.
  if (Boolean(block.deadlineDate) !== Boolean(block.deadlineTime)) return false;

  if (block.kind === 'multiple_choice') {
    if (block.prompt.trim().length === 0) return false;

    const filled = block.answers.filter((a) => a.label.trim().length > 0);
    if (filled.length < MIN_ANSWERS) return false;

    // A colour normally comes from an <input type="color">, which cannot
    // produce anything else — but the API rejects a non-`#rrggbb` value, so
    // catch it here rather than as an opaque 400 on submit.
    if (block.answers.some((a) => a.color !== '' && !HEX_COLOR.test(a.color))) return false;

    if (block.scoringMode === 'per_answer') {
      // The range itself is optional (an organizer may not have decided the
      // stakes yet), but if either bound is given, both must be valid
      // integers with min <= max.
      const hasMin = block.minPoints.trim() !== '';
      const hasMax = block.maxPoints.trim() !== '';
      if (hasMin && !isIntegerString(block.minPoints)) return false;
      if (hasMax && !isIntegerString(block.maxPoints)) return false;
      if (hasMin && hasMax && Number(block.minPoints) > Number(block.maxPoints)) return false;
    }

    return true;
  }

  if (block.kind === 'date') {
    if (block.prompt.trim().length === 0) return false;

    // There are no answers to check — a date question's "answers" are every day
    // on a calendar. Only the stakes can be malformed, and only in a mode that
    // reads them.
    if (block.dateScoringMode !== 'placement') {
      const hasMin = block.minPoints.trim() !== '';
      const hasMax = block.maxPoints.trim() !== '';
      if (hasMin && !isIntegerString(block.minPoints)) return false;
      if (hasMax && !isIntegerString(block.maxPoints)) return false;
      if (hasMin && hasMax && Number(block.minPoints) > Number(block.maxPoints)) return false;
    }

    return true;
  }

  if (block.kind === 'football') {
    // Every advanced-settings field is optional, but a filled-in one must be
    // a valid non-negative number — same "optional but not garbage" rule
    // the point range above follows.
    for (const field of FOOTBALL_SCORING_FIELDS) {
      const value = block[field];
      if (value.trim() !== '' && !isNonNegativeNumberString(value)) return false;
    }

    if (block.footballSource === 'database') return block.footballMatchId.trim().length > 0;
    return (
      block.footballHomeTeamName.trim().length > 0 && block.footballAwayTeamName.trim().length > 0
    );
  }

  return false;
}

/** A block the person has started but not finished — worth flagging, not silently dropping. */
export function isQuestionBlockStarted(block: QuestionBlockDraft): boolean {
  return (
    block.kind !== '' ||
    block.prompt.trim().length > 0 ||
    block.answers.some((a) => a.label.trim() || a.color || a.emoji.trim() || a.imageUrl) ||
    block.displayFormat !== 'list' ||
    block.resultsFormat !== 'list' ||
    block.minPoints.trim() !== '' ||
    block.maxPoints.trim() !== '' ||
    block.deadlineDate !== '' ||
    block.deadlineTime !== '' ||
    block.allowLockForever ||
    block.dateScoringMode !== 'placement' ||
    block.dateTargetMonth !== '' ||
    block.footballCompetitionId !== '' ||
    block.footballTeamId !== '' ||
    block.footballMatchId !== '' ||
    block.footballHomeTeamName.trim() !== '' ||
    block.footballAwayTeamName.trim() !== '' ||
    FOOTBALL_SCORING_FIELDS.some((field) => block[field].trim() !== '')
  );
}

/** Only the advanced-settings fields the organizer actually filled in — the rest fall back to the server's own defaults. */
function footballScoringOverrides(block: QuestionBlockDraft) {
  return {
    ...(block.footballOutcome.trim() !== '' ? { matchOutcome: Number(block.footballOutcome) } : {}),
    ...(block.footballExact.trim() !== '' ? { matchExact: Number(block.footballExact) } : {}),
    ...(block.footballPotMultiplier.trim() !== ''
      ? { matchPotMultiplier: Number(block.footballPotMultiplier) }
      : {}),
    ...(block.footballDecay.trim() !== '' ? { matchDecay: Number(block.footballDecay) } : {}),
    ...(block.footballThreshold.trim() !== ''
      ? { matchThreshold: Number(block.footballThreshold) }
      : {}),
    ...(block.footballPenaltyExact.trim() !== ''
      ? { matchPenaltyExact: Number(block.footballPenaltyExact) }
      : {}),
    ...(block.footballPenaltyWrong.trim() !== ''
      ? { matchPenaltyWrong: Number(block.footballPenaltyWrong) }
      : {}),
  };
}

export function toQuestionDraft(block: QuestionBlockDraft): QuestionDraft {
  const deadlineAt =
    block.deadlineDate && block.deadlineTime
      ? new Date(`${block.deadlineDate}T${block.deadlineTime}`).toISOString()
      : undefined;
  const allowLockForever = block.allowLockForever ? true : undefined;

  if (block.kind === 'football') {
    if (block.footballSource === 'database') {
      return {
        kind: 'football',
        source: 'database',
        sourceMatchId: block.footballMatchId,
        ...(deadlineAt ? { deadlineAt } : {}),
        ...(allowLockForever ? { allowLockForever } : {}),
        ...footballScoringOverrides(block),
      };
    }
    return {
      kind: 'football',
      source: 'custom',
      homeTeamName: block.footballHomeTeamName.trim(),
      awayTeamName: block.footballAwayTeamName.trim(),
      canGoToPenalties: block.footballCanGoToPenalties,
      ...(deadlineAt ? { deadlineAt } : {}),
      ...(allowLockForever ? { allowLockForever } : {}),
      ...footballScoringOverrides(block),
    };
  }

  if (block.kind === 'date') {
    const paysPoints = block.dateScoringMode !== 'placement';
    return {
      kind: 'date',
      prompt: block.prompt.trim(),
      dateScoringMode: block.dateScoringMode,
      // `<input type="month">` yields `YYYY-MM`; the API wants a real calendar
      // day, so anchor it to the 1st — which is all `dateTargetMonth` means.
      ...(block.dateTargetMonth ? { dateTargetMonth: `${block.dateTargetMonth}-01` } : {}),
      ...(paysPoints && block.minPoints.trim() !== ''
        ? { minPoints: Number(block.minPoints) }
        : {}),
      ...(paysPoints && block.maxPoints.trim() !== ''
        ? { maxPoints: Number(block.maxPoints) }
        : {}),
      // A date question defaults to the standings table server-side, so only a
      // deliberate switch away from it needs sending.
      ...(block.resultsFormat !== 'leaderboard'
        ? { resultsFormat: 'list' as QuestionResultsFormat }
        : {}),
      ...(deadlineAt ? { deadlineAt } : {}),
      ...(allowLockForever ? { allowLockForever } : {}),
    };
  }

  const filled = block.answers.filter((a) => a.label.trim().length > 0);
  return {
    kind: 'multiple_choice',
    prompt: block.prompt.trim(),
    scoringMode: block.scoringMode,
    // Cosmetics travel even when the format is `list`, which ignores them:
    // an organizer who configures cards and then switches back to a list
    // while deciding shouldn't lose the work.
    options: filled.map((a) => ({
      label: a.label.trim(),
      ...(a.color ? { color: a.color } : {}),
      ...(a.emoji.trim() ? { emoji: a.emoji.trim() } : {}),
      ...(a.imageUrl ? { imageUrl: a.imageUrl } : {}),
    })),
    ...(block.displayFormat !== 'list' ? { displayFormat: block.displayFormat } : {}),
    ...(block.resultsFormat !== 'list' ? { resultsFormat: block.resultsFormat } : {}),
    ...(block.scoringMode === 'per_answer' && block.minPoints.trim() !== ''
      ? { minPoints: Number(block.minPoints) }
      : {}),
    ...(block.scoringMode === 'per_answer' && block.maxPoints.trim() !== ''
      ? { maxPoints: Number(block.maxPoints) }
      : {}),
    ...(deadlineAt ? { deadlineAt } : {}),
    ...(allowLockForever ? { allowLockForever } : {}),
  };
}
